/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import type { ReactElement } from 'react';

import type ImgixComponent from 'js/components/Imgix';

import { Grid, Link, Typography2, breakpoints } from '@coursera/cds-core';

import Markdown from 'bundles/cms/components/Markdown';
import { isExternalLink } from 'bundles/common/utils/urlUtils';
import Container from 'bundles/degree-description/components/shared/Container';
import { useDegreeDescriptionPageTheme } from 'bundles/degree-description/degreeDescriptionPageTheme';
import { useDegreeDescriptionEventingContext } from 'bundles/degree-description/providers/DegreeDescriptionEventingProvider';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';

const styles = {
  background: css`
    padding: var(--cds-spacing-600) 0;
  `,

  image: () => css`
    ${breakpoints.up('md')} {
      max-width: 400px;
    }

    margin-left: auto;
    margin-right: auto;

    img {
      width: 100%;
    }
  `,
  hideOnMobile: () => css`
    ${breakpoints.down('sm')} {
      display: none;
    }
  `,
  hideOnDesktop: () => css`
    ${breakpoints.up('md')} {
      display: none;
    }
  `,
};

type Props = {
  title?: string;
  description?: string;
  ctaLabel?: string;
  ctaLink?: string;
  image?: ReactElement<ImgixComponent>;
};

export const UpsellsAboutModule = ({ title, description, ctaLabel, ctaLink, image }: Props) => {
  const theme = useDegreeDescriptionPageTheme();
  const { trackClickButton } = useDegreeDescriptionEventingContext();

  const handleOnClick = () => {
    trackClickButton('degree_info_link', 'upsell', ctaLink, ctaLabel);
  };

  return (
    <div css={styles.background}>
      <Container>
        <Grid container justifyContent="space-between" alignItems="center">
          {image && (
            <Grid item sm={12} css={[styles.image, styles.hideOnDesktop]}>
              {image}
            </Grid>
          )}

          {description && title && (
            <Grid item container direction="column" spacing={24} sm={12} md={6}>
              <Grid item>
                <Typography2 variant="titleSmall" css={theme.mobileFontSizeStyle?.header} component="h2">
                  {title}
                </Typography2>
              </Grid>
              <Grid item>
                <Markdown source={description} />
              </Grid>
              <Grid item>
                {ctaLabel && ctaLink && (
                  <Link
                    href={ctaLink}
                    target={isExternalLink(ctaLink) ? '_blank' : undefined}
                    rel={isExternalLink(ctaLink) ? 'noopener noreferrer' : undefined}
                    variant="quiet"
                    typographyVariant="subtitleMedium"
                    component={TrackedLink2}
                    trackingName="upsells_about"
                    onClick={handleOnClick}
                  >
                    {ctaLabel}
                  </Link>
                )}
              </Grid>
            </Grid>
          )}

          {image && (
            <Grid item sm={12} md={6} css={[styles.image, styles.hideOnMobile]}>
              {image}
            </Grid>
          )}
        </Grid>
      </Container>
    </div>
  );
};

export default React.memo(UpsellsAboutModule);
